import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useAuth } from "src/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "src/components/ui/EmptyState";
import { ROUTES } from "src/routes/RouteConfig";
import LoadingSpinner from "src/components/LoadingSpinner";
import { useProfileStore } from "src/store/profileStore";

/* --------------------- Timezone Abbreviation Map --------------------- */
const tzAbbrevMap: Record<string, string> = {
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  MST: "America/Denver",
  MDT: "America/Denver",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  EST: "America/New_York",
  EDT: "America/New_York",
  CST6CDT: "America/Mexico_City",
  IST: "Asia/Kolkata",
};

const resolveTz = (tz: string): string => tzAbbrevMap[tz.toUpperCase()] ?? tz;

/* --------------------- Types --------------------- */
type AvailabilitySlot = {
  day_of_week: number;
  start_time: string;
  end_time: string;
  status: "available" | "auto_accept" | "manual_approval";
};

type Row = {
  dow: number;
  start: string;
  end: string;
};

const zones = [
  { key: "PST", label: "Pacific", abbrev: "PST", color: "bg-blue-900" },
  { key: "MST", label: "Mountain", abbrev: "MST", color: "bg-blue-700" },
  { key: "CST", label: "Central", abbrev: "CST", color: "bg-blue-500" },
  { key: "EST", label: "Eastern", abbrev: "EST", color: "bg-yellow-400" },
  { key: "CST6CDT", label: "Mexico City", abbrev: "CST6CDT", color: "bg-red-500" },
] as const;

/* --------------------- Helpers --------------------- */
const validateTimeString = (timeString: string): boolean => {
  if (!timeString || typeof timeString !== 'string') {
    return false;
  }
  
  // Check if it matches HH:MM or HH:MM:SS format
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?$/;
  return timeRegex.test(timeString);
};

const parseTimeString = (timeString: string): { hours: number; minutes: number; seconds: number } | null => {
  if (!validateTimeString(timeString)) {
    console.warn(`Invalid time format: "${timeString}". Expected format: HH:MM or HH:MM:SS`);
    return null;
  }
  
  try {
    const parts = timeString.split(":").map(Number);
    const hours = parts[0];
    const minutes = parts[1];
    const seconds = parts[2] || 0;
    
    // Additional validation for parsed numbers
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      console.warn(`Invalid time components in: "${timeString}"`);
      return null;
    }
    
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
      console.warn(`Time components out of range in: "${timeString}"`);
      return null;
    }
    
    return { hours, minutes, seconds };
  } catch (error) {
    console.warn(`Error parsing time string "${timeString}":`, error);
    return null;
  }
};

const toUtcInterval = (startTime: string, endTime: string, dow: number, tutorTimezone: string = "UTC") => {
  try {
    // Validate day of week
    if (dow < 0 || dow > 6 || !Number.isInteger(dow)) {
      console.warn(`Invalid day of week: ${dow}. Expected integer 0-6.`);
      return null;
    }
    
    // Parse and validate time strings
    const startTimeParsed = parseTimeString(startTime);
    const endTimeParsed = parseTimeString(endTime);
    
    if (!startTimeParsed || !endTimeParsed) {
      console.warn(`Failed to parse time strings: start="${startTime}", end="${endTime}"`);
      return null;
    }
    
    // Use a specific week where Sunday = Jan 7, 2024 (dow = 0)
    const baseDate = new Date(2024, 0, 7); // January 7, 2024 is a Sunday
    const targetDate = new Date(baseDate.getTime() + dow * 24 * 60 * 60 * 1000);
    
    // Create dates in tutor's timezone using parsed time components
    const startInTutorTz = new Date(targetDate);
    startInTutorTz.setHours(startTimeParsed.hours, startTimeParsed.minutes, startTimeParsed.seconds, 0);
    
    let endInTutorTz = new Date(targetDate);
    endInTutorTz.setHours(endTimeParsed.hours, endTimeParsed.minutes, endTimeParsed.seconds, 0);

    // Fix: if end is before start (e.g., wraps to next day), adjust
    if (endInTutorTz <= startInTutorTz) {
      endInTutorTz = new Date(endInTutorTz.getTime() + 24 * 60 * 60 * 1000);
    }

    // Validate timezone
    if (!tutorTimezone || typeof tutorTimezone !== 'string') {
      console.warn(`Invalid timezone: "${tutorTimezone}". Using UTC as fallback.`);
      tutorTimezone = "UTC";
    }

    // Convert from tutor's timezone to UTC with error handling
    try {
      const utcStart = fromZonedTime(startInTutorTz, tutorTimezone);
      const utcEnd = fromZonedTime(endInTutorTz, tutorTimezone);
      
      // Validate the resulting dates
      if (isNaN(utcStart.getTime()) || isNaN(utcEnd.getTime())) {
        console.warn(`Invalid dates after timezone conversion: start=${utcStart}, end=${utcEnd}`);
        return null;
      }
      
      return { utcStart, utcEnd };
    } catch (timezoneError) {
      console.warn(`Error converting timezone "${tutorTimezone}":`, timezoneError);
      // Fallback to UTC
      try {
        const utcStart = fromZonedTime(startInTutorTz, "UTC");
        const utcEnd = fromZonedTime(endInTutorTz, "UTC");
        return { utcStart, utcEnd };
      } catch (fallbackError) {
        console.error(`Critical error in timezone conversion:`, fallbackError);
        return null;
      }
    }
  } catch (error) {
    console.error(`Unexpected error in toUtcInterval:`, error);
    return null;
  }
};

const intervalsForRow = (row: Row, tutorTimezone: string = "UTC") => {
  try {
    const utcInterval = toUtcInterval(row.start, row.end, row.dow, tutorTimezone);
    
    // If toUtcInterval returns null due to malformed data, skip this row
    if (!utcInterval) {
      console.warn(`Skipping malformed availability slot:`, row);
      return [];
    }
    
    const { utcStart, utcEnd } = utcInterval;

    return zones.flatMap((z) => {
      try {
        const tz = resolveTz(z.abbrev);
        const start = toZonedTime(utcStart, tz);
        const end = toZonedTime(utcEnd, tz);
        
        // Validate the converted dates
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          console.warn(`Invalid dates after timezone conversion for zone ${z.key}:`, { start, end });
          return [];
        }
        
        // Calculate which days this interval spans
        const startDay = start.getDay();
        const endDay = end.getDay();
        
        // If the interval doesn't cross midnight, return single interval
        if (startDay === endDay && start.getDate() === end.getDate()) {
          return [{ zone: z, start, end, targetDow: startDay }];
        }
        
        // If interval crosses midnight, split into multiple day segments
        const intervals = [];
        
        // First day: from start time to end of day
        const endOfFirstDay = new Date(start);
        endOfFirstDay.setHours(23, 59, 59, 999);
        
        if (start < endOfFirstDay) {
          intervals.push({
            zone: z,
            start,
            end: endOfFirstDay,
            targetDow: startDay
          });
        }
        
        // Second day: from start of day to end time
        const startOfSecondDay = new Date(end);
        startOfSecondDay.setHours(0, 0, 0, 0);
        
        if (end > startOfSecondDay) {
          intervals.push({
            zone: z,
            start: startOfSecondDay,
            end,
            targetDow: endDay
          });
        }
        
        return intervals;
      } catch (zoneError) {
        console.warn(`Error processing zone ${z.key}:`, zoneError);
        return [];
      }
    });
  } catch (error) {
    console.error(`Error in intervalsForRow:`, error, row);
    return [];
  }
};

const hourToPercent = (d: Date) => {
  let hours = d.getHours() + d.getMinutes() / 60;
  
  // Handle day wrapping: if hours are negative, add 24
  // if hours are >= 24, subtract 24
  while (hours < 0) hours += 24;
  while (hours >= 24) hours -= 24;
  
  return (hours / 24) * 100;
};

const weekdayLabel = (dow: number): string => {
  // Create a date where Sunday = 0, Monday = 1, etc.
  // January 7, 2024 is a Sunday (dow = 0)
  const date = new Date(2024, 0, 7 + dow);
  return format(date, "ccc");
};

/* --------------------- Component --------------------- */
export default function AvailabilityOverlapChart() {
  const { profileData: authProfileData, loading: authLoading, isInitialized, user, userType, isOnboarded } = useAuth();
  const { profileData: storeProfileData, isLoading: profileLoading } = useProfileStore();
  const navigate = useNavigate();

  const [visibleZones, setVisibleZones] = useState<(typeof zones)[number]["key"][]>(
    () => ["CST", "EST"]
  );
  const [visibleDays, setVisibleDays] = useState(() =>
    Array.from({ length: 7 }, (_, i) => i),
  );

  // Use the most up-to-date profile data (prefer store over auth context)
  const profileData = (storeProfileData as any)?.id ? storeProfileData : authProfileData;

  // Loading state logic - following the same pattern as Dashboard's session loading
  // We're loading availability data if:
  // 1. Auth is not initialized yet
  // 2. Auth is still loading 
  // 3. Profile is still loading
  // 4. We have an onboarded tutor but no profile data yet (data should be available)
  const isLoadingAvailability = !isInitialized || 
                               authLoading || 
                               profileLoading ||
                               (user && userType === 'tutor' && isOnboarded && !(profileData as any)?.id);

  // Check if we have availability data
  // Cast to any to access availabilitySlots which is set by AuthContext for tutors
  const availabilitySlots = (profileData as any)?.availabilitySlots || [];
  const hasAvailabilityData = Array.isArray(availabilitySlots) && availabilitySlots.length > 0;

  const toggleZone = (key: (typeof zones)[number]["key"]) => {
    setVisibleZones((prev) =>
      prev.includes(key) ? prev.filter((z) => z !== key) : [...prev, key]
    );
  };

  const toggleDay = (dow: number) => {
    setVisibleDays((prev) =>
      prev.includes(dow) ? prev.filter((d) => d !== dow) : [...prev, dow],
    );
  };

  // Get tutor's timezone from profile data, fallback to UTC
  const tutorTimezone = profileData?.timezone || "UTC";

  // Convert availability slots to the format expected by the chart
  const availabilityRows: Row[] = React.useMemo(() => {
    // Don't process data while loading
    if (isLoadingAvailability) {
      return [];
    }

    if (!hasAvailabilityData) {
      return [];
    }

    return availabilitySlots
      .filter((slot: AvailabilitySlot) => {
        // Validate each slot before processing
        try {
          // Check if required fields exist and are valid
          if (typeof slot.day_of_week !== 'number' ||
              slot.day_of_week < 0 ||
              slot.day_of_week > 6 ||
              !Number.isInteger(slot.day_of_week)) {
            console.warn(`Invalid day_of_week in availability slot:`, slot);
            return false;
          }

          if (!validateTimeString(slot.start_time)) {
            console.warn(`Invalid start_time in availability slot:`, slot);
            return false;
          }

          if (!validateTimeString(slot.end_time)) {
            console.warn(`Invalid end_time in availability slot:`, slot);
            return false;
          }

          return true;
        } catch (error) {
          console.warn(`Error validating availability slot:`, slot, error);
          return false;
        }
      })
      .map((slot: AvailabilitySlot) => ({
        dow: slot.day_of_week,
        start: slot.start_time,
        end: slot.end_time,
      }));
  }, [isLoadingAvailability, hasAvailabilityData, availabilitySlots]);

  // Generate all intervals for all rows, then group by target day
  const allIntervals = availabilityRows.flatMap((row, rowIndex) => 
    intervalsForRow(row, tutorTimezone).map(interval => ({ ...interval, rowIndex, originalDow: row.dow }))
  );
  
  const groupedByTargetDay = Array.from({ length: 7 }, (_, dow) =>
    allIntervals.filter(interval => interval.targetDow === dow)
  );

  // Render loading state - following Dashboard pattern
  if (isLoadingAvailability) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
        <span className="ml-2 text-gray-600">Loading availability data...</span>
      </div>
    );
  }

  // Render empty state if no availability data - following Dashboard pattern
  if (!availabilityRows.length) {
    return (
      <EmptyState
        title="No availability set"
        description="Set your availability to see timezone overlaps and help students find the best time to book sessions with you."
        svgPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        actionText="Set Availability"
        onActionClick={() => navigate(ROUTES.TUTOR_SCHEDULE.path)}
        className="py-8"
      />
    );
  }

  // Render the chart
  return (
    <div className="w-full max-w-5xl mx-auto p-4 mt-8">

      {/* Filters: Timezones */}
      <div className="mb-4 flex flex-wrap gap-3 justify-center">
        {zones.map((z) => (
          <button
            key={z.key}
            onClick={() => toggleZone(z.key)}
            className={`px-2 py-1 rounded text-xs font-medium border transition ${
              visibleZones.includes(z.key)
                ? `${z.color} text-white`
                : "text-gray-500 border-gray-300"
            }`}
          >
            {z.key}
          </button>
        ))}
      </div>

      {/* Filters: Days */}
      <div className="mb-2 flex flex-wrap gap-2 justify-center text-xs text-gray-600">
        {Array.from({ length: 7 }, (_, i) => (
          <button
            key={i}
            onClick={() => toggleDay(i)}
            className={`px-2 py-0.5 rounded transition ${
              visibleDays.includes(i)
                ? "bg-gray-800 text-white"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {weekdayLabel(i)}
          </button>
        ))}
      </div>

      {/* Time ruler */}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-16 shrink-0"></div>
        <div className="relative flex-1 h-4">
          {Array.from({ length: 7 }, (_, i) => {
            // 0: 12 AM, 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24
            const hour = i * 4;
            const label = hour === 0 ? "12\u00A0AM" : hour === 24 ? "24" : `${hour}`;
            const position = (i / 6) * 100;
            
            let style: React.CSSProperties = {};
            if (i === 0) {
              style = { left: '0%', transform: 'none' };
            } else if (i === 6) {
              style = { left: '100%', transform: 'translateX(-100%)' };
            } else {
              style = { left: `${position}%`, transform: 'translateX(-50%)' };
            }
            
            return (
              <span
                key={i}
                className="absolute text-xs text-gray-500"
                style={style}
              >
                {label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Chart rows */}
      <div className="space-y-2">
        {groupedByTargetDay.map((intervals, dow) => {
          if (!visibleDays.includes(dow)) return null;

          return (
            <div key={dow} className="flex items-center gap-2">
              <div className="w-16 shrink-0 text-right pr-1 text-sm font-medium text-gray-700">
                {weekdayLabel(dow)}
              </div>

              <div className="relative flex-1 h-6 bg-gray-100 rounded">
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 w-px bg-gray-200"
                    style={{ left: `${(i + 1) * (100 / 6)}%` }}
                  />
                ))}

                {intervals
                  .filter(({ zone }) => visibleZones.includes(zone.key))
                  .map(({ zone, start, end, rowIndex, originalDow }, idx) => {
                    let startPercent = hourToPercent(start);
                    let endPercent = hourToPercent(end);
                    
                    let width = endPercent - startPercent;
                    // Clamp bar to not cross 100%
                    if (startPercent + width > 100) {
                      width = 100 - startPercent;
                    }
                    if (startPercent > 100 || width <= 0) {
                      return null;
                    }
                    
                    return (
                      <div
                        key={`${dow}-${originalDow}-${rowIndex}-${zone.key}-${idx}`}
                        className={`${zone.color} absolute top-0 h-full rounded-sm opacity-80 hover:opacity-100 hover:scale-[1.03] transition-transform`}
                        style={{ left: `${startPercent}%`, width: `${width}%` }}
                        title={`${zone.label}: ${format(start, "HH:mm")} – ${format(end, "HH:mm")} (from ${weekdayLabel(originalDow)})`}
                      />
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}